<template>
  <div>
    <div :class="$style.headerWrapper">
      <HgButton
        icon="Filter"
        theme="secondary-bordered"
        :size="ButtonSize.Small"
        :class="$style.filterButton"
        @click="handleModalOpen"
      />
      <AddressSearch
        :location="searchLocation"
        allowClear
        noLabel
        @onSelectedLocationChange="handleSelectedLocationChange"
        @onClearLocation="handleLocationClear"
      />
    </div>
    <FiltersModal
      :visible="isSearchModalVisible"
      @onCancelClick="handleModalClose"
      @onSearchComplete="handleModalClose"
    />
  </div>
</template>

<script setup lang="ts">
import { ButtonSize, HgButton } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import AddressSearch from '@/components/search-bar/address-search/AddressSearch.vue';
import FiltersModal from '@/components/search-bar/small-screen/FiltersModal.vue';
import useRouterLocationPush from '@/composables/useRouterLocationPush';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import { trackLocationSearch } from '@/tracking';
import { EventSource } from '@/types';

const SEARCH_MODAL_OPEN_BODY_CSS_CLASS = 'search-modal-open';

const { pushLocation } = useRouterLocationPush();
const geoStore = useGeoStore();
const mapStore = useMapStore();
const { searchLocation, getIaziLevelByGeoType } = storeToRefs(geoStore);
const { propertyType } = storeToRefs(mapStore);

const isSearchModalVisible = ref<boolean>(false);

const handleSelectedLocationChange = async (location: GeoLocation | null): Promise<void> => {
  mapStore.setLoading(true);
  await geoStore.setSearchLocation(location);
  mapStore.setCurrentLevel(getIaziLevelByGeoType.value);
  await pushLocation(EventSource.Search);
  trackLocationSearch(searchLocation.value!.id, propertyType.value);
};

const handleLocationClear = async (): Promise<void> => {
  mapStore.setLoading(true);
  await pushLocation(EventSource.Reset);
};

const handleModalOpen = (): void => {
  isSearchModalVisible.value = true;

  // TODO: can be removed after HgModal supports to insert to body
  document.body.classList.add(SEARCH_MODAL_OPEN_BODY_CSS_CLASS);
};

const handleModalClose = (): void => {
  isSearchModalVisible.value = false;
  document.body.classList.remove(SEARCH_MODAL_OPEN_BODY_CSS_CLASS);
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

.headerWrapper {
  display: flex;
  gap: $space-50;

  > div:last-child {
    flex: 1;
  }

  [class*='HgInput_wrapper'] {
    border-color: $color-grey-100;
  }
}

.filterButton {
  min-width: 44px;
  border-radius: $border-radius;

  &[class*='HgButton_secondary-bordered'] {
    color: $color-grey-120;
    border-color: $color-grey-120;

    &:active {
      background-color: $color-white;
    }
  }

  &[class*='HgButton_hasIcon'] {
    padding-right: $space-50;
  }

  span[class*='HgButton_content'] {
    display: none;
  }
}
</style>
